import moment from 'moment';
import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  AuthorizationStatus,
  CredentialV2TypeEnum,
} from '~/domain/Credentials/types/Credentials';
import type {
  IAuthorization,
  IAuthorizationCredential,
  ICredentialNpiData,
  ICredentialNumberData,
} from '~/domain/Credentials/types/Credentials';

interface IVerifiedCredentialsProps {
  authorizations: IAuthorization[];
}

const getCredentialDetails = (credential: IAuthorizationCredential) => {
  if (
    credential.type === CredentialV2TypeEnum.Number &&
    credential.data &&
    'number' in credential.data
  ) {
    const numberData = credential?.data as ICredentialNumberData;

    return {
      licenseNumber: numberData?.number?.credentialNumber,
      issuedYear: credential?.issuedAt ? moment(credential.issuedAt).format('YYYY') : null,
    };
  }

  if (
    credential.type === CredentialV2TypeEnum.Npi &&
    credential.data &&
    'npi' in credential.data
  ) {
    const numberData = credential?.data as ICredentialNpiData;

    return {
      licenseNumber: numberData?.npi?.npiNumber,
    };
  }
  return null;
};

const CredentialItem = ({ credential }: { credential: IAuthorizationCredential }) => {
  const details = getCredentialDetails(credential);

  return (
    <Row align="center" gap={2}>
      <Typography as="span" size="body_large" color={colors.neutral[600]}>
        {'\u2022'}
      </Typography>
      <Row align="center" gap={2} wrap="wrap">
        <Typography as="span" size="body_large" color={colors.neutral[600]}>
          {credential.credentialName.replaceAll('NPI Number', 'NPI License').replaceAll(' Number', '')}
        </Typography>
        {details?.licenseNumber ? (
          <Typography as="span" size="heading_4" color={colors.neutral[200]}>
            |
          </Typography>
            ) : null}
        {details?.licenseNumber ? (
          <Row align="center" gap={1}>
            <Typography as="span" bold size="body_large" color={colors.neutral[600]}>
              License #:
            </Typography>
            <Typography as="span" size="body_large" color={colors.neutral[600]}>
              {details?.licenseNumber}
            </Typography>
          </Row>
            ) : null}
        {details?.licenseNumber && details.issuedYear ? (
          <Typography as="span" size="heading_4" color={colors.neutral[200]}>
            |
          </Typography>
            ) : null}
        {details?.issuedYear ? (
          <Row align="center" gap={1}>
            <Typography as="span" bold size="body_large" color={colors.neutral[600]}>
              Issued:
            </Typography>
            <Typography as="span" size="body_large" color={colors.neutral[600]}>
              {details.issuedYear}
            </Typography>
          </Row>
            ) : null}
      </Row>
    </Row>
  );
};

export const VerifiedCredentials = ({
  authorizations,
}: IVerifiedCredentialsProps) => {
  const verifiedCredentials = authorizations
    .flatMap((auth) =>
      (auth.credentials ?? []).filter(
        (c) => c.status === AuthorizationStatus.Verified && !c.isDraft,
      ),
    )
    .filter(
      (c, index, self) =>
        self.findIndex((other) => other.credentialName === c.credentialName) === index,
    );

  if (verifiedCredentials.length === 0) {
    return null;
  }

  return (
    <Column gap={1}>
      <Typography as="span" bold size="heading_4" color={colors.neutral[600]}>
        Verified Credentials:
      </Typography>
      <Column gap={2} pl={1}>
        {verifiedCredentials.map((credential) => (
          <CredentialItem key={credential.id ?? credential.credential} credential={credential} />
        ))}
      </Column>
    </Column>
  );
};
